import get from 'lodash.get';
import { Selector } from 'reselect';

// Global Types
import { AppState } from 'services/types';

// Selectors
// @ts-ignore
import { selectRoles } from 'services/roles/selectors';
// @ts-ignore
import { selectUsers } from 'services/people/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';

// Utils
// @ts-ignore
import createDeepEqualSelector from 'utils/reselect/createDeepEqualSelector';

// Types
import { UserMe } from 'services/usersMe/types';
import { Role } from 'services/roles/types';
import { Person } from 'services/people/types';

const selectUsersMe = (state: AppState): UserMe => state.usersMe;

const selectRequiredRoles = (state: any, props: any): any =>
    props.requiredRoles; // add typing

const selectUserPerson = createDeepEqualSelector(
    [selectUsersMe, selectUsers],
    (usersMeState: UserMe, usersState: Record<Person['id'], Person>) => {
        const personDetails = get(usersState, usersMeState.email);
        return { ...personDetails, roleMappings: usersMeState.roleMappings };
    }
);

const makeSelectUsersMePersonValue = <K extends keyof UserMe, V extends UserMe>(
    field: K
): Selector<AppState, V[K]> =>
    createDeepEqualSelector(selectUserPerson, (userPersonState: V) =>
        get(userPersonState, field)
    );

const makeSelectUsersMeValue = <K extends keyof UserMe, V extends UserMe>(
    field: K
): Selector<AppState, V[K]> =>
    createDeepEqualSelector(selectUsersMe, (usersMeState: V) =>
        get(usersMeState, field)
    );

const selectUserRoleCodes = createDeepEqualSelector(
    [selectUserPerson, selectRoles, selectCurrentOrganizationId],
    (
        userPersonState: UserMe,
        rolesState: Role[],
        currentOrganizationId: string
    ) => {
        if (!userPersonState || !userPersonState.roleMappings) {
            return undefined;
        }

        return userPersonState.roleMappings
            .filter(
                (roleMapping) =>
                    roleMapping.organizationId === currentOrganizationId
            )
            .map((roleMapping) => {
                const role = get(rolesState, roleMapping.roleId);
                if (role && role.code) {
                    return role.code;
                }

                if (role && role.name) {
                    // Legacy support for roles that don't have a code, converts name into code format
                    return role.name.toUpperCase().split(' ').join('_');
                }

                return null;
            });
    }
);

const selectUserRolesArray = createDeepEqualSelector(
    selectUserRoleCodes,
    (userRoleCodesState: Record<string, any>[]): Record<string, any>[] =>
        userRoleCodesState
);

const selectIsManager = createDeepEqualSelector(
    selectUserRoleCodes,
    (userRoleCodesState: any): boolean =>
        userRoleCodesState && userRoleCodesState.includes('MANAGER')
);

const selectIsAdmin = createDeepEqualSelector(
    selectUserRoleCodes,
    makeSelectUsersMeValue('isSuperAdmin'),
    (userRoleCodesState: any, isSuperAdmin: boolean): boolean => {
        return (
            (userRoleCodesState && userRoleCodesState.includes('ADMIN')) ||
            isSuperAdmin
        );
    }
);

const selectIsAssistantManager = createDeepEqualSelector(
    selectUserRoleCodes,
    (userRoleCodesState: any): boolean =>
        userRoleCodesState && userRoleCodesState.includes('ASSISTANT_MANAGER')
);

const selectIsFieldStaff = createDeepEqualSelector(
    selectUserRoleCodes,
    (userRoleCodesState: any): boolean =>
        userRoleCodesState && userRoleCodesState.includes('FIELD_STAFF')
);

const selectIsSupervisor = createDeepEqualSelector(
    selectUserRoleCodes,
    (userRoleCodesState: any): boolean =>
        userRoleCodesState && userRoleCodesState.includes('SUPERVISOR')
);

const selectIsPayroll = createDeepEqualSelector(
    selectUserRoleCodes,
    (userRoleCodesState: any): boolean =>
        userRoleCodesState && userRoleCodesState.includes('PAYROLL')
);

const makeSelectUserHasPermission = (): any =>
    createDeepEqualSelector(
        selectUserRoleCodes,
        selectRequiredRoles,
        makeSelectUsersMeValue('isSuperAdmin'),
        (
            userRoleCodesState: any,
            requiredRoles: any,
            isSuperAdminState: boolean
        ): boolean =>
            (userRoleCodesState &&
                requiredRoles.some((role: string) =>
                    userRoleCodesState.includes(role)
                )) ||
            isSuperAdminState
    );

export {
    selectUsersMe,
    selectUserPerson,
    makeSelectUsersMePersonValue,
    selectIsManager,
    makeSelectUsersMeValue,
    selectIsAdmin,
    selectIsAssistantManager,
    selectIsFieldStaff,
    selectIsSupervisor,
    selectIsPayroll,
    selectUserRolesArray,
    makeSelectUserHasPermission,
    selectUserRoleCodes
};
